import React, { useState, useEffect, useCallback } from "react";
import { Modal, Select, SegmentedControl, Text } from "@mantine/core";
import { ConfirmationPlanogramViewer } from "./ConfirmationPlanogramViewer";
import { useQuery } from "@tanstack/react-query";
import {
  mvpApiGetPlanogramProductOptions,
  mvpApiListPlanogramsOptions,
} from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useAtlasStores } from "@labeling-tools/api/hooks/atlas/useAtlasStores";
import {
  setPlanogramModalSource,
  setPlanogramOverrideId,
  setPlanogramSelectedProductId,
} from "@/store/index";
import { useActivePlanoProductInfoStore } from "@labeling-tools/ui-components/src/planogram-viewer/use-active-plano-product-info-store";
import { PlanogramProduct } from "@labeling-tools/ui-components/src/PlanogramProduct";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import { PlanogramDetailSchema } from "@labeling-tools/api/atlas";
import { OosPlanogramModalActions } from "./OosPlanogramModalActions";
import { useTimeTracking } from "@/contexts/TimeTrackingContext";
import { ViewMode } from "@/types";

export const PlanogramModal = ({
  storeId,
  capturedImage,
  emptyAreaImage,
  planogramData,
  task,
  planogramSelectedProductId,
  zoomToProductId,
}: {
  storeId: string;
  capturedImage: React.ReactNode;
  emptyAreaImage?: React.ReactNode;
  task: JobTaskDetailSchema;
  planogramSelectedProductId: string | null;
  zoomToProductId?: string;
  planogramData: PlanogramDetailSchema;
  mode?: ViewMode;
}) => {
  const dispatch = useAppDispatch();
  const { recordQ3Interaction } = useTimeTracking();
  const [opened, setOpened] = useState(false);
  const [imageView, setImageView] = useState<"product" | "empty_area">(
    "product",
  );

  // The override committed to redux (set only when the labeler saves a product
  // on a different planogram). Reset on submit via the global `reset()`.
  const committedOverride = useAppSelector(
    (state) => state.oos.planogramOverrideId,
  );
  // The planogram currently selected in the modal dropdown. This is local so a
  // cancelled/exited modal never leaks a selection into the task result.
  const [displayOverride, setDisplayOverride] = useState<string | null>(null);
  // Local edit wins while the modal is open, otherwise show the committed value.
  const planogramIdOverride = displayOverride ?? committedOverride;
  const planogramId = planogramData?.id;
  const { data: atlasStores, isLoading: atlasStoresLoading } =
    useAtlasStores(storeId);

  const planogramModalSource = useAppSelector(
    (state) => state.oos.planogramModalSource,
  );

  // zustand state vars
  const activePlanogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.product_id,
  );
  const planogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.planogram_product_id,
  );
  const resetActivePlanoProductInfoStore = useActivePlanoProductInfoStore(
    (s) => s.reset,
  );

  // query planograms
  const { data, isLoading: planogramsQueryLoading } = useQuery({
    ...mvpApiListPlanogramsOptions({
      query: {
        store_id: atlasStores?.stores[0]?.id,
        product_id: planogramSelectedProductId,
        finalized: true,
      },
    }),
    enabled: !!atlasStores?.stores[0]?.id && !!planogramSelectedProductId,
  });
  const planogramsLoading = planogramsQueryLoading || atlasStoresLoading;

  const planograms = data?.planograms?.length
    ? [
        planogramData,
        ...data.planograms.filter(
          (p) =>
            p.id !== planogramData.id &&
            p.description !== planogramData.description,
        ),
      ]
    : [planogramData];

  // planogram product - used for displaying the product details
  const { data: selectedProduct, isLoading: selectedProductLoading } = useQuery(
    {
      ...mvpApiGetPlanogramProductOptions({
        path: {
          planogram_id: planogramIdOverride || planogramId || "",
          planogram_product_id: planogramProductId || "",
        },
      }),
      enabled:
        (!!planogramIdOverride || !!planogramId) && !!activePlanogramProductId,
    },
  );

  const close = useCallback(() => {
    dispatch(setPlanogramSelectedProductId(null));
    dispatch(setPlanogramModalSource(null));
    setOpened(false);
    setDisplayOverride(null);
    resetActivePlanoProductInfoStore();
  }, [
    dispatch,
    setPlanogramSelectedProductId,
    setPlanogramModalSource,
    resetActivePlanoProductInfoStore,
  ]);

  const handleSave = useCallback(() => {
    recordQ3Interaction();
    dispatch(
      setPlanogramOverrideId(
        planogramIdOverride && planogramIdOverride !== planogramId
          ? planogramIdOverride
          : null,
      ),
    );
    close();
  }, [dispatch, planogramIdOverride, planogramId, close, recordQ3Interaction]);

  // Open the modal and sync the image view when a planogram source is set (either icon)
  useEffect(() => {
    if (planogramModalSource !== null) {
      setOpened(true);
      setImageView(
        planogramModalSource === "empty_area" ? "empty_area" : "product",
      );
    }
  }, [planogramModalSource]);

  const handlePlanogramOverride = (value: string | null) => {
    setDisplayOverride(value);
  };

  const showImageToggle = !!emptyAreaImage;
  const isEmptyAreaSource = planogramModalSource === "empty_area";

  return (
    <Modal
      opened={opened}
      size="100%"
      onClose={close}
      title="Planogram Viewer"
      withinPortal={false}
      data-planogram-modal
    >
      {!!planograms?.length && (
        <div className="mb-4">
          <Select
            value={planogramIdOverride || planogramId}
            onChange={handlePlanogramOverride}
            data={planograms?.map((planogram) => ({
              value: planogram.id || "",
              label: planogram.description || "",
            }))}
            size="md"
            w={400}
          />
        </div>
      )}
      <div className="flex">
        <div className="w-1/3 mr-4">
          {showImageToggle && (
            <div className="mb-3">
              <SegmentedControl
                value={imageView}
                onChange={(v) => setImageView(v as "product" | "empty_area")}
                data={[
                  { value: "empty_area", label: "Empty Area" },
                  { value: "product", label: "Product Location" },
                ]}
                size="xs"
                fullWidth
              />
            </div>
          )}
          {imageView === "empty_area" && emptyAreaImage
            ? emptyAreaImage
            : capturedImage}
        </div>
        <div className="w-2/3 relative">
          {planogramId && !planogramsLoading && (
            <ConfirmationPlanogramViewer
              planogramId={planogramIdOverride || planogramId}
              initialProductId={planogramSelectedProductId || zoomToProductId}
              zoomOnlyInitialProduct={
                !isEmptyAreaSource &&
                !planogramSelectedProductId &&
                !!zoomToProductId
              }
              wideInitialZoom={isEmptyAreaSource}
            />
          )}
          {!planogramId && !planogramIdOverride && (
            <div>
              <Text>No planogram found</Text>
            </div>
          )}

          {/* Product Details */}
          <PlanogramProduct
            selectedProduct={selectedProduct}
            loading={selectedProductLoading}
            key={activePlanogramProductId}
          />

          {/* actions */}
          {!!activePlanogramProductId &&
            (task?.type === "OUT_OF_STOCK_CONFIRMATION" ||
              task?.type === "OUT_OF_STOCK_CONFIRMATION_BENCHMARK") && (
              <OosPlanogramModalActions
                onClose={close}
                onSave={handleSave}
                selectedProductId={activePlanogramProductId}
              />
            )}
        </div>
      </div>
    </Modal>
  );
};
